/**
 * Activity → Reviews (admins): the executives' review requests, newest first. The admin answers once (Reply…),
 * or changes the template and marks the request done (Mark done…, optional note). "Fill his draft" (owner
 * 10-21 (6)) opens the executive's draft in the fill form; "Open the template" goes to the template editor.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { cleanReviewText, type ReviewState } from '@saledeed/shared';
import { Button, ErrorBox, Field, formatDateTime, Modal, Select, Spinner } from '../components/ui';
import { listReviews, markReviewDone, replyReview, type Review } from './api';
import { reviewTextState, ReviewTextBox } from './AskReviewDialog';
import { ReviewTag } from './ReviewTag';

const STATES: { value: ReviewState | 'all'; label: string }[] = [
  { value: 'open', label: 'Open' }, { value: 'answered', label: 'Answered' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' },
];

export function ReviewsTab({ templateCode, onClearTemplate }: { templateCode?: string | undefined; onClearTemplate: () => void }) {
  const [state, setState] = useState<ReviewState | 'all'>('open');
  const [replying, setReplying] = useState<Review | null>(null);
  const [finishing, setFinishing] = useState<Review | null>(null);
  const reviews = useQuery({
    queryKey: ['reviews', state, templateCode ?? ''],
    queryFn: () => listReviews({ state, ...(templateCode ? { templateCode } : {}) }),
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-600">
        Questions executives sent while filling. Answer once with <b>Reply…</b>, or change the template (edit and publish) and press
        <b> Mark done…</b>. A document already started keeps its version; the executive is told to start a new one.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Show">{id => (
          <Select id={id} value={state} onChange={e => setState(e.target.value as ReviewState | 'all')}>
            {STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        )}</Field>
        {templateCode && (
          <p className="flex items-center gap-2 text-sm">
            Template <code className="text-xs">{templateCode}</code>
            <Button tone="ghost" onClick={onClearTemplate}>Show every template</Button>
          </p>
        )}
      </div>
      <ErrorBox error={reviews.error} />
      {reviews.isPending ? <Spinner label="Loading review requests" /> : (
        <ul className="flex flex-col gap-2" aria-label="Review requests">
          {reviews.data?.map(r => (
            <li key={r.id} data-review={r.id} className="flex flex-col gap-1 rounded border border-slate-200 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <ReviewTag state={r.state} />
                <span className="font-medium">{r.templateTitle}</span>
                <code className="text-xs">{r.templateCode}</code>
                <span className="text-xs text-slate-500">
                  asked on v{r.templateVersion}{r.currentVersion !== null && r.currentVersion !== r.templateVersion ? ` (latest v${r.currentVersion})` : ''}
                </span>
              </div>
              <p className="text-xs text-slate-500">
                {r.createdBy.displayName} · {r.createdBy.userCode} · in {r.projectName} · {formatDateTime(r.createdAt)}
              </p>
              <p className="whitespace-pre-wrap">{r.message}</p>
              {r.reply !== null && (
                <p className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-slate-700">
                  <span className="block text-xs font-medium text-slate-500">{r.state === 'answered' ? 'Reply' : 'Note'}{r.repliedBy ? ` by ${r.repliedBy.displayName}` : ''}:</span>
                  {r.reply}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {r.documentState === 'draft' && (
                  <Link to="/projects/$id/documents/$docId" params={{ id: r.projectId, docId: r.documentId }}
                    className="rounded bg-blue-700 px-2 py-1 text-sm font-medium text-white hover:bg-blue-800"
                    title={`Open ${r.createdBy.displayName}'s draft in the fill form and fill it for him`}>Fill his draft</Link>
                )}
                <Link to="/templates/$id" params={{ id: r.templateId }} className="text-sm text-blue-800 underline"
                  title="Change the template itself (for every new document)">Open the template</Link>
                {r.state === 'open' && <Button onClick={() => setReplying(r)}>Reply…</Button>}
                {r.state !== 'done' && <Button onClick={() => setFinishing(r)}>Mark done…</Button>}
              </div>
            </li>
          ))}
          {!reviews.data?.length && <li className="py-6 text-center text-slate-500">No review requests here.</li>}
        </ul>
      )}
      {replying && <ReplyDialog review={replying} onClose={() => setReplying(null)} />}
      {finishing && <DoneDialog review={finishing} onClose={() => setFinishing(null)} />}
    </div>
  );
}

function useAfterChange(onClose: () => void) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['reviews'] });
    void qc.invalidateQueries({ queryKey: ['review-counts'] });
    void qc.invalidateQueries({ queryKey: ['audit'] });
    onClose();
  };
}

function ReplyDialog({ review, onClose }: { review: Review; onClose: () => void }) {
  const [text, setText] = useState('');
  const done = useAfterChange(onClose);
  const send = useMutation({ mutationFn: () => replyReview(review.id, cleanReviewText(text)), onSuccess: done });
  const ok = reviewTextState(text).sendable;
  return (
    <Modal open title="Reply to the review request" onClose={onClose}>
      <form className="flex flex-col gap-3 text-sm" onSubmit={e => { e.preventDefault(); if (ok) send.mutate(); }}>
        <p className="whitespace-pre-wrap rounded bg-slate-50 p-2">{review.message}</p>
        <ReviewTextBox label="Your reply" value={text} onChange={setText} hint="One answer; it cannot be changed later. No ID numbers or client details." />
        <ErrorBox error={send.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" tone="primary" disabled={!ok || send.isPending}>Send reply</Button>
        </div>
      </form>
    </Modal>
  );
}

function DoneDialog({ review, onClose }: { review: Review; onClose: () => void }) {
  const [text, setText] = useState('');
  const done = useAfterChange(onClose);
  // A note only on an open request (an answered one keeps its reply). A blank box sends no note.
  const withNote = review.state === 'open';
  const state = reviewTextState(text);
  const ok = !withNote || state.blank || state.sendable;
  const send = useMutation({ mutationFn: () => markReviewDone(review.id, withNote && !state.blank ? cleanReviewText(text) : undefined), onSuccess: done });
  return (
    <Modal open title="Mark the review request done" onClose={onClose}>
      <form className="flex flex-col gap-3 text-sm" onSubmit={e => { e.preventDefault(); if (ok) send.mutate(); }}>
        <p>Use this when you changed the template (edit and publish) or nothing more is needed.</p>
        {withNote && (
          <ReviewTextBox label="Note for the executive (optional)" value={text} onChange={setText} rows={3}
            hint={'For example "I changed the template." Leave it empty for no note.'} />
        )}
        <ErrorBox error={send.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" tone="primary" disabled={!ok || send.isPending}>Mark done</Button>
        </div>
      </form>
    </Modal>
  );
}
